import { useEffect, useState, type ReactNode } from "react";
import clsx from "clsx";
import { Calculator, Check, Clock, Settings2, SlidersHorizontal, Sparkles } from "lucide-react";

import { Modal } from "@/components/Modal";
import {
  CUSTOM_MAX_SECONDS,
  CUSTOM_MIN_SECONDS,
  INTERVAL_OPTIONS,
  METHOD_OPTIONS,
  aggregationBadge,
  resolveAggregation,
} from "@/lib/aggregation";
import { useUi, type Aggregation } from "@/lib/store";

type CustomUnit = "minutes" | "hours" | "days";
const UNIT_SECONDS: Record<CustomUnit, number> = { minutes: 60, hours: 3600, days: 86400 };

function splitSeconds(seconds: number): { value: number; unit: CustomUnit } {
  if (seconds % 86400 === 0) return { value: seconds / 86400, unit: "days" };
  if (seconds % 3600 === 0) return { value: seconds / 3600, unit: "hours" };
  return { value: Math.round(seconds / 60), unit: "minutes" };
}

/**
 * How every dashboard chart reduces its data.
 *
 * Auto picks an interval from the range and uses Greatest - the signed
 * largest value in each bucket, which is how a structural response is read.
 * Manual exposes the interval, the method and a rolling window.
 */
export function AggregationDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { aggregation, setAggregation, range } = useUi();
  const [draft, setDraft] = useState<Aggregation>(aggregation);
  const [customValue, setCustomValue] = useState(15);
  const [customUnit, setCustomUnit] = useState<CustomUnit>("minutes");

  useEffect(() => {
    if (!open) return;
    setDraft(aggregation);
    const split = splitSeconds(aggregation.customSeconds);
    setCustomValue(split.value);
    setCustomUnit(split.unit);
  }, [open, aggregation]);

  const customSeconds = Math.round(customValue * UNIT_SECONDS[customUnit]);
  const customInvalid =
    draft.mode === "manual" &&
    draft.interval === "custom" &&
    (!Number.isFinite(customSeconds) ||
      customSeconds < CUSTOM_MIN_SECONDS ||
      customSeconds > CUSTOM_MAX_SECONDS);

  const effective = resolveAggregation({ ...draft, customSeconds }, range);
  const raw = draft.mode === "manual" && draft.interval === "none";

  function save() {
    if (customInvalid) return;
    setAggregation({ ...draft, customSeconds });
    onClose();
  }

  return (
    <Modal
      title="Aggregation Configuration"
      subtitle={`Applies to every chart · currently ${aggregationBadge(resolveAggregation(aggregation, range))}`}
      open={open}
      onClose={onClose}
      width="max-w-xl"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={save} disabled={customInvalid}>
            Save
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-ink-700">
          {(["auto", "manual"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={draft.mode === mode}
              onClick={() => setDraft({ ...draft, mode })}
              className={clsx(
                "flex items-center justify-center gap-2 py-2 text-sm transition-colors",
                draft.mode === mode
                  ? "bg-brand-600/20 text-brand-200"
                  : "text-ink-300 hover:bg-ink-800",
              )}
            >
              {mode === "auto" ? (
                <Sparkles className="h-4 w-4" />
              ) : (
                <SlidersHorizontal className="h-4 w-4" />
              )}
              {mode === "auto" ? "Auto" : "Manual"}
            </button>
          ))}
        </div>

        {draft.mode === "auto" ? (
          <p className="text-xs leading-relaxed text-ink-400">
            Picks the interval from the time range: every raw sample up to 2 days, hourly up
            to a month, daily beyond that, weekly past 15 years. The method is{" "}
            <b className="text-ink-200">Greatest</b> — the signed value with the largest
            magnitude in each interval, so a negative peak is shown as negative. For this
            range that gives <b className="text-ink-200">{aggregationBadge(effective)}</b>.
          </p>
        ) : (
          <>
            <Section icon={<Clock className="h-3.5 w-3.5" />} title="Interval">
              <div className="flex flex-wrap gap-2">
                {INTERVAL_OPTIONS.map((o) => (
                  <Chip
                    key={o.key}
                    active={draft.interval === o.key}
                    onClick={() => setDraft({ ...draft, interval: o.key })}
                  >
                    {o.label}
                  </Chip>
                ))}
              </div>
              {draft.interval === "custom" && (
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    className="input w-28"
                    value={customValue}
                    onChange={(e) => setCustomValue(Number(e.target.value))}
                    aria-label="Custom interval length"
                  />
                  <select
                    className="input w-32"
                    value={customUnit}
                    onChange={(e) => setCustomUnit(e.target.value as CustomUnit)}
                    aria-label="Custom interval unit"
                  >
                    <option value="minutes">minutes</option>
                    <option value="hours">hours</option>
                    <option value="days">days</option>
                  </select>
                  {customInvalid && (
                    <span className="text-[11px] text-sev-1">Between 1 minute and 366 days.</span>
                  )}
                </div>
              )}
            </Section>

            {!raw && (
              <Section icon={<Calculator className="h-3.5 w-3.5" />} title="Method">
                <div className="flex flex-wrap gap-2">
                  {METHOD_OPTIONS.map((o) => (
                    <Chip
                      key={o.key}
                      active={draft.method === o.key}
                      onClick={() => setDraft({ ...draft, method: o.key })}
                    >
                      {o.label}
                    </Chip>
                  ))}
                </div>
              </Section>
            )}

            {!raw && (
              <Section icon={<Settings2 className="h-3.5 w-3.5" />} title="Options">
                <label className="flex items-start gap-2 text-sm text-ink-200">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-brand-500"
                    checked={draft.rolling}
                    onChange={(e) => setDraft({ ...draft, rolling: e.target.checked })}
                  />
                  <span>
                    Use rolling aggregation
                    <span className="block text-[11px] text-ink-500">
                      A trailing window at every sample instead of fixed buckets. A rolling
                      month is 30 days.
                    </span>
                  </span>
                </label>
              </Section>
            )}

            {raw && (
              <p className="text-xs text-ink-400">
                Every stored sample is plotted. Over a long range a channel with more points
                than a chart can hold is thinned, keeping each slot&rsquo;s highest and lowest
                values, and the chart says so.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div>
      <div className="label mb-2 flex items-center gap-1.5">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors",
        active
          ? "border-brand-500 bg-brand-600/20 text-brand-200"
          : "border-ink-700 bg-ink-800 text-ink-200 hover:border-ink-600",
      )}
    >
      {active && <Check className="h-3 w-3" />}
      {children}
    </button>
  );
}
